/**
 * Inventory status renderers. Maps the equipment/component lifecycle status to a
 * tone-coloured `StatusPill` with plain-language labels for sales/ops staff.
 * Item-type Chip comes from the shared masters-status helper (do not duplicate).
 */
import type { ReactNode } from 'react';
import { Chip } from '@vnetwork/ui/primitives';
import { StatusPill, type StatusTone } from '@vnetwork/ui/data';
import type { EquipmentStatus, ItemCondition } from '@/api/capacities';

interface StatusMeta {
  tone: StatusTone;
  label: string;
}

const STATUS_META: Record<EquipmentStatus, StatusMeta> = {
  available: { tone: 'info', label: 'Available' },
  in_use: { tone: 'success', label: 'In Use' },
  loaned: { tone: 'warning', label: 'Loaned' },
  in_datacenter: { tone: 'success', label: 'In Data Center' },
  in_service: { tone: 'success', label: 'In Service' },
  sold: { tone: 'muted', label: 'Sold' },
  damaged: { tone: 'danger', label: 'Damaged' },
  maintenance: { tone: 'warning', label: 'Maintenance' },
};

/** Human-readable label for an equipment status (fallback: title-cased raw). */
export function itemStatusLabel(status?: EquipmentStatus | string): string {
  if (!status) return '';
  const meta = STATUS_META[status as EquipmentStatus];
  return meta ? meta.label : status.replace(/_/g, ' ');
}

/** Equipment/component status → tone-coloured `StatusPill`. */
export function itemStatusCell(status?: EquipmentStatus | string): ReactNode {
  if (!status) return <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>;
  const meta = STATUS_META[status as EquipmentStatus];
  return (
    <StatusPill tone={meta?.tone ?? 'neutral'}>
      {meta?.label ?? status.replace(/_/g, ' ')}
    </StatusPill>
  );
}

const CONDITION_META: Record<ItemCondition, { tone: 'success' | 'info' | 'warning' | 'danger'; label: string }> = {
  new: { tone: 'success', label: 'New' },
  used: { tone: 'info', label: 'Used' },
  refurbished: { tone: 'warning', label: 'Refurbished' },
  damaged: { tone: 'danger', label: 'Damaged' },
};

/** Item condition ("new" | "used" | …) → tinted Chip. */
export function itemConditionCell(condition?: ItemCondition | string): ReactNode {
  if (!condition) return <span style={{ color: 'var(--vn-fg-muted)' }}>—</span>;
  const meta = CONDITION_META[condition as ItemCondition];
  return (
    <Chip tone={meta?.tone ?? 'neutral'}>{meta?.label ?? condition.replace(/_/g, ' ')}</Chip>
  );
}
